import { useEffect, useRef, useState } from 'react'
import { registerMenuEscape } from './overlayStack'

export interface ActionMenuItem {
  label: string
  icon?: string
  onClick: () => void
  disabled?: boolean
  /** Zweite, gedämpfte Zeile unter dem Namen — bei einem gesperrten Eintrag der Grund, sichtbar statt nur im Tooltip. */
  hint?: string
}

interface ActionMenuProps {
  items: ActionMenuItem[]
  /** Accessible label for the icon-only trigger. */
  label?: string
  /** Menu anchor edge relative to the trigger. Default 'right' (menu grows leftward). */
  align?: 'left' | 'right'
  /** Beschriftet den Auslöser mit Text („··· Fassung") statt nur mit dem Symbol; `label` bleibt sein Name für Screenreader. */
  triggerText?: string
  /** Optional roving keyboard focus for the publishing flow. */
  keyboardNavigation?: boolean
  /** Touch size (M30 QM, 44 px target): a round 44 px trigger and 44 px menu rows with 14 px text. Default off. */
  touch?: boolean
}

/**
 * "More actions" overflow menu — for secondary/rare actions (settings,
 * one-time imports) that would otherwise compete for space with the primary
 * actions in a page header. Icon-only trigger (more_vert). DESIGN.md §8c —
 * no existing shared component covered this, so it's added here.
 */
export function ActionMenu({ items, label = 'Weitere Aktionen', align = 'right', triggerText, keyboardNavigation = false, touch = false }: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [isOpen])

  useEffect(() => {
    if (isOpen && keyboardNavigation) menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus()
  }, [isOpen, keyboardNavigation])

  // Escape schließt NUR das Menü, nie den Drawer/das Modal darum (overlayStack: das offene Menü steht obenauf).
  useEffect(() => {
    if (!isOpen) return
    if (!keyboardNavigation) return registerMenuEscape(() => setIsOpen(false))
    return registerMenuEscape(() => { setIsOpen(false); triggerRef.current?.focus() })
  }, [isOpen, keyboardNavigation])

  return (
    <div className="relative inline-block" ref={wrapRef} onBlur={e => { if (keyboardNavigation && !e.currentTarget.contains(e.relatedTarget)) setIsOpen(false) }}>
      <button
        ref={triggerRef}
        onKeyDown={e => { if (keyboardNavigation && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); setIsOpen(true) } }}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        className={`${triggerText ? 'h-9 px-3 gap-1.5 text-xs font-medium' : touch ? 'h-11 w-11 rounded-full' : 'w-9 h-9'} flex items-center justify-center ${touch ? '' : 'rounded-lg'} border transition-colors ${
          isOpen ? 'border-primary text-primary' : 'border-outline-variant text-on-surface-variant hover:text-on-surface hover:bg-surface-container-low'
        }`}
      >
        <span className="material-symbols-outlined !text-[18px]">{triggerText ? 'more_horiz' : 'more_vert'}</span>
        {triggerText}
      </button>
      {isOpen && (
        <div
          role="menu"
          ref={menuRef}
          onKeyDown={e => {
            if (!keyboardNavigation || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
            e.preventDefault()
            const enabled = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'))
            const index = enabled.indexOf(document.activeElement as HTMLButtonElement)
            const target = e.key === 'Home' ? 0 : e.key === 'End' ? enabled.length - 1 : (index + (e.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length
            enabled[target]?.focus()
          }}
          className={`absolute z-50 top-full mt-1 ${align === 'right' ? 'right-0' : 'left-0'} min-w-[200px] ${items.some((i) => i.hint) ? 'w-[300px] max-w-[85vw]' : ''} rounded-lg shadow-lg overflow-hidden bg-surface-container-lowest border border-outline-variant`}
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              tabIndex={keyboardNavigation ? -1 : undefined}
              disabled={item.disabled}
              onClick={() => { item.onClick(); setIsOpen(false) }}
              className={`flex ${item.hint ? 'items-start' : 'items-center'} gap-2 w-full text-left px-3 ${touch ? 'min-h-11 py-2 text-sm' : 'py-2 text-xs'} font-medium transition-colors ${
                item.disabled ? 'text-on-surface-variant/40 cursor-not-allowed' : 'text-on-surface hover:bg-surface-container-low'
              }`}
            >
              {item.icon && <span className="material-symbols-outlined !text-[16px] text-on-surface-variant">{item.icon}</span>}
              <span className="min-w-0">
                <span className="block">{item.label}</span>
                {item.hint && <span className="mt-0.5 block text-[11px] font-normal text-on-surface-variant">{item.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
